import type { AnimationItem, LottiePlayer } from 'lottie-web';
import { useCallback, useEffect, useRef, useState } from 'react';

import { LottieAnimationOptions } from './LottieAnimationOptions';

const lottieFilePath = '/static/libs/lottie-web@5.11.0.min.js';

type Args = {
  src: string;
  options?: LottieAnimationOptions;
};

if (typeof window !== 'undefined') {
  // @ts-expect-error Add to global namespace type
  global.lottiePromise =
    // @ts-expect-error Add to global namespace type
    global.lottiePromise ||
    new Promise((resolve, reject) => {
      if (document.head.querySelector(`script[src="${lottieFilePath}"]`)) {
        resolve(undefined);
      } else {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        script.src = lottieFilePath;
        document.head.appendChild(script);
      }
    });
}

/**
 * Lazy load the lottie library and the JSON animation to avoid increasing the
 * first paint bundle.
 */
export function useLottieAnimation({ src, options = {} }: Args) {
  const animationContainerRef = useRef<HTMLDivElement>(null);

  const [lottieAnimation, setLottieAnimation] = useState<AnimationItem>();
  const [animationReady, setAnimationReady] = useState(false);
  const loopCountRef = useRef(1);

  const [lottieLoaded, setLottieLoaded] = useState(
    Boolean(global.window && window.lottie),
  );

  const animationPlayerRef = useRef<LottiePlayer>();
  const playAnimation = useCallback(() => {
    animationPlayerRef.current?.play();
  }, []);

  useEffect(() => {
    // @ts-expect-error Add to global namespace type
    global.lottiePromise
      .then(() => {
        setLottieLoaded(true);
      })
      .catch((error: Error) => {
        if (error instanceof Error) {
          throw new Error(error.message);
        }
      });
  }, []);

  useEffect(() => {
    if (animationReady && lottieAnimation) {
      if (options.reverse) {
        lottieAnimation.setDirection(-1);
      } else {
        lottieAnimation.setDirection(1);
      }
      if (!options.noAutoplay) {
        setTimeout(() => lottieAnimation.play(), 1);
      }
    }
  }, [
    animationReady,
    lottieAnimation,
    options.reverse,
    options.noAutoplay,
    playAnimation,
  ]);

  useEffect(() => {
    if (!lottieLoaded) return;

    // @ts-expect-error Ensure TS knows lottie can't be undefined here
    const animation = window.lottie.loadAnimation({
      // @ts-expect-error Figure out issue with ref type here
      container: animationContainerRef.current,
      renderer: 'svg',
      loop: !options?.maxLoopCount || options.maxLoopCount !== 1,
      path: src,
      autoplay: !options?.noAutoplay,
    });

    animation.addEventListener('data_ready', () => {
      setAnimationReady(true);
      if (!options.reverse) animation.goToAndStop(animation.totalFrames, true);
      if (!options.noAutoplay) setTimeout(() => animation.play(), 1);
    });

    animation.addEventListener('loopComplete', () => {
      if (!options.maxLoopCount) return;

      loopCountRef.current += 1;

      const loopCount = loopCountRef.current;
      if (loopCount >= options.maxLoopCount) animation.setLoop(false);
    });

    setLottieAnimation(animation);

    return () => animation.destroy();
    // @NOTE - adding `options.reverse` to the deps breaks the animation
    // We could pass it as a ref to this effect, or find another way to handle this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lottieLoaded, options.maxLoopCount, options.noAutoplay, src]);

  return {
    lottieAnimation,
    animationContainerRef,
    animationReady,
    playAnimation,
  };
}
